import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  BarChart3,
  Briefcase,
  CheckCircle,
  FileText,
  Flame,
  ListChecks,
  Target,
  TrendingUp,
  X,
} from "lucide-react";
import { toast } from "react-hot-toast";
import { useAuth } from "../../contexts/AuthContext";
import { firebaseApp } from "../../firebase";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  getFirestore,
  query,
  where,
} from "firebase/firestore";
import { safeToDate } from "../../utils/SafeToDate";


// Lightweight interactive line chart for HR metrics (Enhanced UI)
const PerformanceLineChart = ({ data }) => {
  const [hoverIndex, setHoverIndex] = useState(null);

  const width = 1000;
  const height = 400;
  const padding = 60;
  const graphHeight = height - padding * 2;
  const graphWidth = width - padding * 2;

  const metrics = [
    { key: "jobs", label: "Jobs Posted", color: "#2563eb" },       // Blue
    { key: "tests", label: "Tests Created", color: "#10b981" },    // Green
    { key: "candidates", label: "Candidates", color: "#ef4444" },  // Red
  ];

  const maxY = Math.max(
    10, // Minimum scale of 10
    ...data.flatMap((d) => [d.jobs || 0, d.tests || 0, d.candidates || 0])
  );

  const getCoordinates = (idx, value, metricOffset = 0) => {
    const xStep = data.length > 1 ? graphWidth / (data.length - 1) : 0;
    const x = padding + idx * xStep + metricOffset;
    const y = height - padding - (value / maxY) * graphHeight;
    return { x, y };
  };

  const pointsForMetric = (key, metricIndex) =>
    data.map((point, idx) => {
      // Offset each line slightly to prevent complete overlap when values are identical
      const offset = (metricIndex - 1) * 3;
      const { x, y } = getCoordinates(idx, point[key] || 0, offset);
      return { x, y, label: point.week, value: point[key] || 0 };
    });

  const handleMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    // Calculate relative X coordinate scaled to SVG coordinate system
    const scaleX = width / rect.width;
    const mouseX = (e.clientX - rect.left) * scaleX;

    // Find nearest point
    const xStep = data.length > 1 ? graphWidth / (data.length - 1) : 0;
    const relativeX = mouseX - padding;
    const idx = Math.max(0, Math.min(data.length - 1, Math.round(relativeX / (xStep || 1))));

    if (idx !== hoverIndex) setHoverIndex(idx);
  };

  const hoverPoint = hoverIndex != null ? data[hoverIndex] : null;
  const xStep = data.length > 1 ? graphWidth / (data.length - 1) : 0;
  const hoverX = hoverIndex != null ? padding + hoverIndex * xStep : null;

  return (
    <div className="w-full">
      <div className="flex items-center gap-6 mb-6 justify-center sm:justify-start">
        {metrics.map((m) => (
          <div key={m.key} className="flex items-center gap-2 text-sm font-medium text-gray-700">
            <span className="h-3 w-8 rounded-full" style={{ background: m.color }}></span>
            <span>{m.label}</span>
          </div>
        ))}
      </div>
      <div className="relative w-full aspect-[2.5/1]">
        {hoverPoint && hoverX != null && (
          <div
            className="pointer-events-none absolute bg-white/95 backdrop-blur-sm shadow-xl border border-gray-100 rounded-xl px-4 py-3 text-sm text-gray-800 transform -translate-x-1/2 transition-all duration-75"
            style={{
              left: `${(hoverX / width) * 100}%`,
              top: '0%',
              zIndex: 20
            }}
          >
            <div className="font-bold text-gray-900 border-b border-gray-100 pb-1 mb-1">{hoverPoint.week}</div>
            <div className="space-y-1">
              <div className="flex items-center justify-between gap-3 text-blue-600"><span>Jobs:</span> <span className="font-bold">{hoverPoint.jobs}</span></div>
              <div className="flex items-center justify-between gap-3 text-green-600"><span>Tests:</span> <span className="font-bold">{hoverPoint.tests}</span></div>
              <div className="flex items-center justify-between gap-3 text-red-600"><span>Candidates:</span> <span className="font-bold">{hoverPoint.candidates}</span></div>
            </div>
          </div>
        )}
        <svg
          viewBox={`0 0 ${width} ${height}`}
          className="w-full h-full"
          onMouseMove={handleMove}
          onMouseLeave={() => setHoverIndex(null)}
          preserveAspectRatio="xMidYMid meet"
        >

          {/* Grid lines */}
          {[0, 25, 50, 75, 100].map((v) => {
            const y = height - padding - (v / 100) * graphHeight;
            return (
              <g key={v}>
                <line x1={padding} y1={y} x2={width - padding} y2={y} stroke="#f3f4f6" strokeWidth={1.5} strokeDasharray="4 4" />
              </g>
            );
          })}

          {/* Y Axis Labels */}
          <text x={padding - 15} y={height - padding} textAnchor="end" className="text-sm fill-gray-400 font-medium">0</text>
          <text x={padding - 15} y={height - padding - graphHeight / 2 + 5} textAnchor="end" className="text-sm fill-gray-400 font-medium">{Math.round(maxY / 2)}</text>
          <text x={padding - 15} y={padding + 5} textAnchor="end" className="text-sm fill-gray-400 font-medium">{Math.ceil(maxY)}</text>

          {/* X Axis labels */}
          {data.map((point, idx) => {
            const x = padding + idx * xStep;
            return (
              <text key={point.week} x={x} y={height - padding + 25} textAnchor="middle" className="text-sm fill-gray-500 font-medium">
                {point.week}
              </text>
            );
          })}

          {/* Lines */}
          {metrics.map((m, mIdx) => {
            const points = pointsForMetric(m.key, mIdx);
            // Line path
            const linePath = points
              .map((p, idx) => `${idx === 0 ? "M" : "L"}${p.x},${p.y}`)
              .join(" ");

            return (
              <g key={m.key}>
                {/* Stroke Line */}
                <path d={linePath} fill="none" stroke={m.color} strokeWidth={4} strokeLinejoin="round" strokeLinecap="round" className="drop-shadow-sm" />
                {/* Dots - Always visible now */}
                {points.map((p, idx) => (
                  <circle
                    key={idx}
                    cx={p.x}
                    cy={p.y}
                    r={5}
                    fill="white"
                    stroke={m.color}
                    strokeWidth={2.5}
                  />
                ))}
              </g>
            );
          })}

          {/* Hover guide line */}
          {hoverX != null && (
            <line x1={hoverX} y1={padding} x2={hoverX} y2={height - padding} stroke="#94a3b8" strokeWidth={1.5} strokeDasharray="4 4" />
          )}
        </svg>
      </div>
    </div>
  );
};

const Dashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const db = getFirestore(firebaseApp);

  const [stats, setStats] = useState({
    jobsPosted: 0,
    testsCreated: 0,
    candidatesReviewed: 0,
    successRate: 0,
  });
  const [graphData, setGraphData] = useState([]);
  const [rawData, setRawData] = useState({ jobs: [], tests: [], candidates: [] });
  const [graphFilter, setGraphFilter] = useState("weeks_7"); // daily, weeks_7, months_3, months_6, year_1

  const [recentActivity, setRecentActivity] = useState([]);
  const [profileCompletion, setProfileCompletion] = useState(0);
  const [profileItems, setProfileItems] = useState([]);
  const [streak, setStreak] = useState({ current: 0, longest: 0 });
  const [loading, setLoading] = useState(true);
  const [showActivityModal, setShowActivityModal] = useState(false);

  // Compute all activity for the modal
  const allActivity = useMemo(() => {
    const { jobs, tests, candidates } = rawData;
    return [...jobs, ...tests, ...candidates].sort(
      (a, b) => b.date.getTime() - a.date.getTime()
    );
  }, [rawData]);


  const stars = useMemo(() => {
    return [...Array(40)].map((_, i) => ({
      id: i,
      top: `${Math.random() * 100}%`,
      left: `${Math.random() * 100}%`,
      size: `${Math.random() * 3 + 1}px`,
      opacity: Math.random() * 0.7 + 0.3,
      duration: `${Math.random() * 2 + 1}s`, // Faster: 1-3s
      delay: `${Math.random() * 2}s`,
    }));
  }, []);

  const computeStreak = (dates) => {
    if (!dates.length) return { current: 0, longest: 0 };
    const uniqueDays = Array.from(
      new Set(
        dates.map((d) =>
          new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime()
        )
      )
    ).sort((a, b) => b - a);

    let current = 1;
    let longest = 1;
    for (let i = 1; i < uniqueDays.length; i++) {
      const diffDays = (uniqueDays[i - 1] - uniqueDays[i]) / (1000 * 60 * 60 * 24);
      if (diffDays === 1) {
        current += 1;
        longest = Math.max(longest, current);
      } else if (diffDays > 1) {
        break; // streak broken
      }
    }
    return { current, longest };
  };

  useEffect(() => {
    const load = async () => {
      if (!user) return;

      try {
        const [jobsSnap, testsSnap, resultsSnap, profileSnap] = await Promise.all([
          getDocs(query(collection(db, "hrJobs"), where("hrId", "==", user.id))),
          getDocs(query(collection(db, "hrTests"), where("hrId", "==", user.id))),
          getDocs(
            query(collection(db, "jobseekerResults"), where("hrId", "==", user.id))
          ),
          getDoc(doc(db, "hrProfiles", user.id)),
        ]);

        const jobs = jobsSnap.docs.map((d) => ({
          type: "Job posted",
          date: safeToDate(d.data().createdAt) || new Date(),
          title: d.data().jobTitle || "Job",
        }));
        const tests = testsSnap.docs.map((d) => ({
          type: "Test created",
          date: safeToDate(d.data().createdAt) || new Date(),
          title: d.data().title || "Test",
        }));
        const candidates = resultsSnap.docs.map((d) => {
          const data = d.data();
          return {
            type: "Candidate reviewed",
            date: safeToDate(data.completedAt) || new Date(),
            title: data.jobTitle || "Candidate",
            score: data.totalScore || data.score || 0,
          };
        });

        // Stats
        const totalScore = candidates.reduce((sum, c) => sum + (c.score || 0), 0);
        const successRate =
          candidates.length > 0 ? Math.round(totalScore / candidates.length) : 0;

        setStats({
          jobsPosted: jobs.length,
          testsCreated: tests.length,
          candidatesReviewed: candidates.length,
          successRate,
        });

        // Store raw data for graph filtering
        setRawData({ jobs, tests, candidates });

        // Recent activity (top 5)
        const activity = [...jobs, ...tests, ...candidates].sort(
          (a, b) => b.date.getTime() - a.date.getTime()
        );
        setRecentActivity(activity.slice(0, 5));

        // Streak from any activity
        const streakDates = activity.map((a) => a.date);
        setStreak(computeStreak(streakDates));

        // Profile completion (Detailed check from hrProfiles)
        const profileData = profileSnap.exists() ? profileSnap.data() : {};

        // Helper to check if a field is meaningful (not empty/null and not default placeholder if applicable)
        const isCompleted = (val) => val && val.trim().length > 0;

        const completionItems = [
          { label: "Name", completed: isCompleted(profileData.name || user.name), weight: 10 },
          { label: "Email", completed: isCompleted(profileData.email || user.email), weight: 10 },
          { label: "Company", completed: isCompleted(profileData.company || user.company), weight: 15 },
          { label: "Department", completed: isCompleted(profileData.department), weight: 10 },
          { label: "Title", completed: isCompleted(profileData.title), weight: 10 },
          { label: "Bio", completed: isCompleted(profileData.bio), weight: 10 },
          { label: "Jobs posted", completed: jobs.length > 0, weight: 15 },
          { label: "Tests created", completed: tests.length > 0, weight: 10 },
          { label: "Phone", completed: isCompleted(profileData.phone || user.phoneNumber), weight: 5 },
          { label: "Location", completed: isCompleted(profileData.location), weight: 5 },
        ];

        const completedWeight = completionItems.reduce(
          (sum, item) => sum + (item.completed ? item.weight : 0),
          0
        );
        const totalWeight = completionItems.reduce((sum, item) => sum + item.weight, 0);
        const completionPercent = Math.round((completedWeight / totalWeight) * 100);
        setProfileCompletion(completionPercent);
        setProfileItems(completionItems);
      } catch (error) {
        console.error("Error loading HR dashboard:", error);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [db, user]);

  // Effect to generate graph data based on filter
  useEffect(() => {
    if (!rawData.jobs.length && !rawData.tests.length && !rawData.candidates.length) return;

    const { jobs, tests, candidates } = rawData;
    const now = new Date();
    let points = [];

    const getInRange = (list, start, end) =>
      list.filter(item => item.date >= start && item.date < end).length;

    if (graphFilter === 'daily') {
      // Last 7 days
      for (let i = 6; i >= 0; i--) {
        const d = new Date(now);
        d.setDate(d.getDate() - i);
        d.setHours(0, 0, 0, 0);

        const nextD = new Date(d);
        nextD.setDate(d.getDate() + 1);

        const dayName = d.toLocaleDateString('en-US', { weekday: 'short' });
        points.push({
          week: dayName, // Using 'week' property as label to match component expectation
          jobs: getInRange(jobs, d, nextD),
          tests: getInRange(tests, d, nextD),
          candidates: getInRange(candidates, d, nextD)
        });
      }
    } else if (graphFilter === 'weeks_7') {
      // Last 7 weeks
      for (let i = 6; i >= 0; i--) {
        const start = new Date(now);
        start.setDate(start.getDate() - (i * 7 + (now.getDay() || 7) - 1)); // Align to weeks approximately or just sliding window
        // Simple sliding window of 7 days
        const weekStart = new Date(now);
        weekStart.setDate(weekStart.getDate() - (i * 7));
        weekStart.setHours(0, 0, 0, 0);
        const weekEnd = new Date(weekStart);
        weekEnd.setDate(weekEnd.getDate() + 7);

        points.push({
          week: `W${7 - i}`,
          jobs: getInRange(jobs, weekStart, weekEnd),
          tests: getInRange(tests, weekStart, weekEnd),
          candidates: getInRange(candidates, weekStart, weekEnd)
        });
      }
    } else if (graphFilter === 'months_3') {
      // Last 12 weeks (approx 3 months) - broken down by week for better detail
      for (let i = 11; i >= 0; i--) {
        const weekStart = new Date(now);
        weekStart.setDate(weekStart.getDate() - (i * 7));
        weekStart.setHours(0, 0, 0, 0);
        const weekEnd = new Date(weekStart);
        weekEnd.setDate(weekEnd.getDate() + 7);

        points.push({
          week: `W${12 - i}`,
          jobs: getInRange(jobs, weekStart, weekEnd),
          tests: getInRange(tests, weekStart, weekEnd),
          candidates: getInRange(candidates, weekStart, weekEnd)
        });
      }
    } else if (graphFilter === 'months_6') {
      // Last 6 months - grouped by month
      for (let i = 5; i >= 0; i--) {
        const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
        const nextMonth = new Date(now.getFullYear(), now.getMonth() - i + 1, 1);

        const monthName = d.toLocaleDateString('en-US', { month: 'short' });
        points.push({
          week: monthName,
          jobs: getInRange(jobs, d, nextMonth),
          tests: getInRange(tests, d, nextMonth),
          candidates: getInRange(candidates, d, nextMonth)
        });
      }
    } else if (graphFilter === 'year_1') {
      // Last 12 months - grouped by month
      for (let i = 11; i >= 0; i--) {
        const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
        const nextMonth = new Date(now.getFullYear(), now.getMonth() - i + 1, 1);

        const monthName = d.toLocaleDateString('en-US', { month: 'short' });
        points.push({
          week: monthName,
          jobs: getInRange(jobs, d, nextMonth),
          tests: getInRange(tests, d, nextMonth),
          candidates: getInRange(candidates, d, nextMonth)
        });
      }
    }

    setGraphData(points);
  }, [rawData, graphFilter]);



  const pendingItems = useMemo(
    () => profileItems.filter((item) => !item.completed),
    [profileItems]
  );

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto">
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading HR dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="relative bg-gradient-to-r from-red-900 via-red-800 to-red-600 text-white rounded-2xl p-4 overflow-hidden shadow-xl border border-red-700/50 group">
        {/* Animated Background Elements */}

        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          {/* Stars */}
          {stars.map((star) => (
            <div
              key={star.id}
              className="absolute bg-white rounded-full animate-pulse transition-opacity"
              style={{
                top: star.top,
                left: star.left,
                width: star.size,
                height: star.size,
                opacity: star.opacity,
                animationDuration: star.duration,
                animationDelay: star.delay,
              }}
            />
          ))}

          {/* Glassy Orbs */}
          <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl -mr-16 -mt-16 pointer-events-none mix-blend-overlay"></div>
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-black/20 rounded-full blur-2xl -ml-12 -mb-12 pointer-events-none mix-blend-soft-light"></div>
        </div>

        <div className="relative z-10">
          <h1 className="text-2xl font-bold mb-1 tracking-tight drop-shadow-sm text-white flex items-center gap-2">
            Welcome Back, <span className="text-white">{user?.name || "HR Manager"}</span>!
          </h1>
          <p className="text-white/90 text-sm font-medium max-w-2xl">
            Here's your hiring command center. Track your performance and manage candidates efficiently.
          </p>
        </div>
      </div>

      {/* Main Grid */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Left Column */}
        < div className="lg:col-span-2 space-y-6" >
          {/* Graph on top */}
          < div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6" >
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-gray-900 flex items-center space-x-2">
                <BarChart3 className="w-5 h-5 text-red-600" />
                <span>Performance Trends</span>
              </h2>
              <div className="relative">
                <select
                  value={graphFilter}
                  onChange={(e) => setGraphFilter(e.target.value)}
                  className="bg-gray-50 border border-gray-200 text-gray-700 text-sm rounded-lg focus:ring-red-500 focus:border-red-500 block p-2 outline-none cursor-pointer hover:bg-gray-100 transition-colors"
                >
                  <option value="daily">Daily</option>
                  <option value="weeks_7">Last 7 Weeks</option>
                  <option value="months_3">Last 3 Months</option>
                  <option value="months_6">Last 6 Months</option>
                  <option value="year_1">Last Year</option>
                </select>
              </div>
            </div>
            <div className="flex items-center justify-center py-4">
              <div className="w-full">
                <PerformanceLineChart data={graphData} />
              </div>
            </div>
          </div >

          {/* Stats Cards */}
          < div className="grid md:grid-cols-4 gap-4" >
            <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Jobs Posted</p>
                  <p className="text-2xl font-bold text-gray-900 mt-1">
                    {stats.jobsPosted}
                  </p>
                </div>
                <Briefcase className="w-8 h-8 text-blue-600" />
              </div>
            </div>
            <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Tests Created</p>
                  <p className="text-2xl font-bold text-gray-900 mt-1">
                    {stats.testsCreated}
                  </p>
                </div>
                <FileText className="w-8 h-8 text-green-600" />
              </div>
            </div>
            <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Candidates Reviewed</p>
                  <p className="text-2xl font-bold text-gray-900 mt-1">
                    {stats.candidatesReviewed}
                  </p>
                </div>
                <ListChecks className="w-8 h-8 text-purple-600" />
              </div>
            </div>
            <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Success Rate</p>
                  <p className="text-2xl font-bold text-gray-900 mt-1">
                    {stats.successRate}%
                  </p>
                </div>
                <TrendingUp className="w-8 h-8 text-red-600" />
              </div>
            </div>
          </div >

          {/* Streak (compact) */}
          < div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4" >
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center space-x-2">
                <Flame className="w-4 h-4 text-orange-500" />
                <span>Streak</span>
              </h2>
              <span className="text-sm text-gray-600">
                Longest: {streak.longest} days
              </span>
            </div>
            <div className="flex items-center space-x-3">
              <div className="px-3 py-2 rounded-lg bg-orange-50 text-orange-700 text-sm font-semibold">
                {streak.current} day streak
              </div>
              <p className="text-sm text-gray-600">
                Stay active daily to grow your streak.
              </p>
            </div>
          </div >
        </div >

        {/* Right Column */}
        < div className="space-y-6" >
          {/* Recent Activity Top 5 */}
          < div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6" >
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-gray-900 flex items-center space-x-2">
                <CheckCircle className="w-5 h-5 text-green-600" />
                <span>Recent Activity (Top 5)</span>
              </h2>
              <button
                className="px-3 py-1.5 text-xs font-bold text-red-600 bg-red-50 hover:bg-red-100 rounded-lg transition-colors border border-red-100"
                onClick={() => setShowActivityModal(true)}
              >
                View All
              </button>
            </div>
            {
              recentActivity.length === 0 ? (
                <p className="text-sm text-gray-500">No activity yet.</p>
              ) : (
                <div className="space-y-3">
                  {recentActivity.map((item, idx) => (
                    <div
                      key={`${item.type}-${idx}`}
                      className="flex items-center justify-between p-3 rounded-lg bg-gray-50 border border-gray-200"
                    >
                      <div>
                        <p className="font-semibold text-gray-900 text-sm">
                          {item.type}
                        </p>
                        <p className="text-xs text-gray-600 truncate max-w-[220px]">
                          {item.title}
                        </p>
                      </div>
                      <span className="text-xs text-gray-500">
                        {item.date?.toLocaleDateString?.() ||
                          new Date(item.date).toLocaleDateString()}
                      </span>
                    </div>
                  ))}
                </div>
              )
            }
          </div >

          {/* Profile completion (only if incomplete) */}
          {
            profileCompletion < 100 && (
              <div className="bg-gradient-to-r from-yellow-50 to-orange-50 border border-yellow-200 rounded-xl p-6">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center space-x-2">
                    <Target className="w-5 h-5 text-yellow-600" />
                    <h3 className="text-lg font-semibold text-gray-900">Profile Completion</h3>
                  </div>
                  <span className="text-xl font-bold text-yellow-600">
                    {profileCompletion}%
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2 mb-3">
                  <div
                    className="h-2 rounded-full bg-gradient-to-r from-yellow-500 to-orange-500 transition-all duration-500"
                    style={{ width: `${profileCompletion}%` }}
                  ></div>
                </div>

                {pendingItems.length > 0 && (
                  <div className="space-y-1.5 mb-3">
                    <p className="text-sm font-semibold text-gray-700">Pending items:</p>
                    {pendingItems.map((item, idx) => (
                      <div
                        key={idx}
                        className="flex items-center justify-between text-sm text-gray-700"
                      >
                        <div className="flex items-center space-x-2">
                          <CheckCircle className="w-4 h-4 text-yellow-600" />
                          <span>{item.label}</span>
                        </div>
                        <span className="text-gray-500 font-medium">{item.weight}%</span>
                      </div>
                    ))}
                  </div>
                )}

                <p className="text-xs text-gray-600 mb-3">
                  Complete your profile to unlock better visibility and collaboration.
                </p>
                <button
                  onClick={() => navigate("/hr/my-profile")}
                  className="w-full px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors text-sm font-semibold"
                >
                  Complete Profile →
                </button>
              </div>
            )
          }
        </div>
      </div>

      {/* Activity Modal */}
      {showActivityModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 backdrop-blur-sm animate-fadeIn">
          <div className="bg-white rounded-2xl w-full max-w-lg max-h-[80vh] flex flex-col shadow-2xl animate-scaleIn">
            <div className="p-4 border-b border-gray-100 flex items-center justify-between bg-gray-50/50 rounded-t-2xl">
              <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                <ListChecks className="w-5 h-5 text-red-600" />
                All Activity
              </h3>
              <button
                onClick={() => setShowActivityModal(false)}
                className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            <div className="overflow-y-auto p-4 space-y-3">
              {allActivity.length > 0 ? (
                allActivity.map((item, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 rounded-xl bg-white border border-gray-100 hover:border-red-100 hover:shadow-sm transition-all">
                    <div className="flex items-start gap-3">
                      <div className={`p-2 rounded-lg ${item.type.includes('Job') ? 'bg-blue-50 text-blue-600' :
                        item.type.includes('Test') ? 'bg-purple-50 text-purple-600' :
                          'bg-green-50 text-green-600'
                        }`}>
                        {item.type.includes('Job') ? <Briefcase className="w-4 h-4" /> :
                          item.type.includes('Test') ? <ListChecks className="w-4 h-4" /> :
                            <CheckCircle className="w-4 h-4" />}
                      </div>
                      <div>
                        <p className="font-bold text-gray-900 text-sm">{item.type}</p>
                        <p className="text-xs text-gray-500 line-clamp-1">{item.title}</p>
                      </div>
                    </div>
                    <span className="text-[10px] font-medium text-gray-400 bg-gray-50 px-2 py-1 rounded-full whitespace-nowrap">
                      {item.date?.toLocaleDateString()}
                    </span>
                  </div>
                ))
              ) : (
                <div className="text-center py-12 text-gray-400 flex flex-col items-center">
                  <ListChecks className="w-12 h-12 mb-3 opacity-20" />
                  <p>No activity found.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;


